import React, { useRef, useEffect, forwardRef, useState } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useRowSelect,
  useGlobalFilter,
  useExpanded,
} from "react-table";
import classNames from "classnames";

// components
import Pagination from "./PaginationComp";
import EmptyData from "./EmptyData";

interface GlobalFilterProps {
  preGlobalFilteredRows: any;
  globalFilter: any;
  setGlobalFilter: any;
  searchBoxClass: any;
}

// Define a default UI for filtering
const GlobalFilter = ({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  searchBoxClass,
}: GlobalFilterProps) => {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = useState<any>(globalFilter);
  const [timer, setTimer] = useState<NodeJS.Timer | null>(null);
  const onChange = (value: string) => {
    if (timer) clearTimeout(timer);
    setTimer(setTimeout(() => setGlobalFilter(value || ""), 1000));
  };

  useEffect(() => {
    setValue(globalFilter);
  }, [globalFilter]);

  return (
    <div className={classNames(searchBoxClass)}>
      <span className="d-flex align-items-center">
        {" "}
        <input
          type="search"
          value={value || ""}
          onChange={(e: any) => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder={`Buscar...`}
          className="form-control w-auto ms-1"
        />
      </span>
    </div>
  );
};

interface IndeterminateCheckboxProps {
  indeterminate: any;
  children?: React.ReactNode;
}

const IndeterminateCheckbox = forwardRef<
  HTMLInputElement,
  IndeterminateCheckboxProps
>(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef();
  const resolvedRef: any = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <>
      <div className="form-check">
        <input
          type="checkbox"
          className="form-check-input"
          ref={resolvedRef}
          {...rest}
        />
        <label htmlFor="form-check-input" className="form-check-label"></label>
      </div>
    </>
  );
});

interface TableProps {
  isSearchable?: boolean;
  isSortable?: boolean;
  // Ordenação atual vinda de fora (ex.: da URL), para o cabeçalho indicá-la
  // inclusive quando ela muda sem um clique na tabela
  sortBy?: { id: string; desc?: boolean }[];
  // Uma coluna por vez: ordenar outra substitui a anterior (mesmo com Shift)
  disableMultiSort?: boolean;
  pagination?: boolean;
  isSelectable?: boolean;
  isExpandable?: boolean;
  sizePerPageList?: {
    text: string;
    value: number;
  }[];
  columns: {
    Header: any;
    accessor: string;
    sort?: boolean;
    Cell?: any;
    className?: string;
    // Classe do cabeçalho da coluna (ex.: o mesmo espaçamento das células)
    headerClassName?: string;
    isSticky?: boolean;
    // Largura definida na montagem; com table-layout: fixed, não muda com o
    // conteúdo (o react-table já usa "width", com padrão 150, em toda coluna)
    fixedWidth?: number | string;
    // Célula da coluna nas sub-linhas (ver getSubRows); recebe item e row
    SubCell?: any;
  }[];
  data: any[];
  pageSize?: any;
  searchBoxClass?: string;
  tableClass?: string;
  // Abaixo desta largura a tabela rola na horizontal (dentro do
  // table-responsive) em vez de espremer as colunas
  tableMinWidth?: number;
  theadClass?: string;
  pageCount?: number;
  changePage?: (page: number) => void;
  setSearch?: React.Dispatch<React.SetStateAction<string>>;
  setOrder?: React.Dispatch<
    React.SetStateAction<{ id: string; desc?: boolean | undefined }[]>
  >;
  searchValue?: string;
  pageIndex?: number;
  rowClassName?: (row: any) => string | undefined;
  // Itens exibidos como sub-linhas logo abaixo da linha, alinhados às mesmas
  // colunas: cada coluna os renderiza com o seu SubCell
  getSubRows?: (row: any) => any[] | undefined;
  // Com ela, as sub-linhas ficam sempre montadas e abrem/fecham com animação
  // (como um acordeão); sem ela, aparecem sempre abertas
  isSubRowsOpen?: (row: any) => boolean;
  // Clique na linha fora de botões, links e campos (ex.: abrir/fechar as
  // sub-linhas, como um acordeão)
  onRowClick?: (row: any) => void;
  // Identidade da linha (padrão: a posição). Com ela, trocar a lista não
  // reaproveita a linha de outro item na mesma posição (ex.: sub-linhas
  // abertas fechando com animação nos dados novos)
  getRowId?: (original: any, index: number) => string;
}

const SELETOR_INTERATIVO =
  "button, a, input, select, textarea, label, [role='button']";

const Table = (props: TableProps) => {
  const isSearchable = props["isSearchable"] || false;
  const isSortable = props["isSortable"] || false;
  const pagination = props["pagination"] || false;
  const isSelectable = props["isSelectable"] || false;
  const isExpandable = props["isExpandable"] || false;
  const sizePerPageList = props["sizePerPageList"] || [];

  let otherProps: any = {};

  if (isSearchable) {
    otherProps["useGlobalFilter"] = useGlobalFilter;
  }
  if (isSortable) {
    otherProps["useSortBy"] = useSortBy;
  }
  if (isExpandable) {
    otherProps["useExpanded"] = useExpanded;
  }
  if (pagination) {
    otherProps["usePagination"] = usePagination;
  }
  if (isSelectable) {
    otherProps["useRowSelect"] = useRowSelect;
  }

  const dataTable = useTable(
    {
      columns: props["columns"],
      data: props["data"],
      ...(props.getRowId
        ? {
            getRowId: (original: any, index: number) =>
              props.getRowId!(original, index),
          }
        : {}),
      initialState: {
        pageSize: props["pageSize"] || 10,
        sortBy: props.sortBy || [],
      },
      manualPagination: pagination,
      autoResetPage: !pagination,
      pageCount: props["pageCount"] || 0,
      manualSortBy: true,
      // Com setSearch, a busca é feita no servidor: refiltrar no navegador com
      // um termo antigo (ex.: limpo de fora) esconderia linhas válidas
      manualGlobalFilter: !!props.setSearch,
      disableMultiSort: props.disableMultiSort,
      stateReducer: (newState, action) => {
        const isChangePageAction = action.type === "gotoPage";
        const isGlobalFilterAction = action.type === "setGlobalFilter";
        const isSortByAction = action.type === "toggleSortBy";

        if (pagination && isChangePageAction && props["changePage"]) {
          const changePage = props["changePage"];
          changePage(action.pageIndex);
        }

        if (
          isSearchable &&
          isGlobalFilterAction &&
          props["setSearch"] &&
          props["changePage"]
        ) {
          const setSearch = props["setSearch"];
          const changePage = props["changePage"];
          changePage(0);
          setSearch(action.filterValue ? action.filterValue : "");
          newState.pageIndex = 0;
        }

        if (isSortable && isSortByAction && props["setOrder"]) {
          const setOrder = props["setOrder"];
          setOrder(newState.sortBy);
        }

        return newState;
      },
    },
    otherProps.hasOwnProperty("useGlobalFilter") &&
      otherProps["useGlobalFilter"],
    otherProps.hasOwnProperty("useSortBy") && otherProps["useSortBy"],
    otherProps.hasOwnProperty("useExpanded") && otherProps["useExpanded"],
    otherProps.hasOwnProperty("usePagination") && otherProps["usePagination"],
    otherProps.hasOwnProperty("useRowSelect") && otherProps["useRowSelect"],
    (hooks) => {
      isSelectable &&
        hooks.visibleColumns.push((columns: any) => [
          // Let's make a column for selection
          {
            id: "selection",
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllPageRowsSelectedProps }: any) => (
              <div>
                <IndeterminateCheckbox
                  {...getToggleAllPageRowsSelectedProps()}
                />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }: any) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...columns,
        ]);

      isExpandable &&
        hooks.visibleColumns.push((columns: any) => [
          // Let's make a column for selection
          {
            // Build our expander column
            id: "expander", // Make sure it has an ID
            Header: ({
              getToggleAllRowsExpandedProps,
              isAllRowsExpanded,
            }: any) => (
              <span {...getToggleAllRowsExpandedProps()}>
                {isAllRowsExpanded ? "-" : "+"}
              </span>
            ),
            Cell: ({ row }) =>
              // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
              // to build the toggle for expanding a row
              row.canExpand ? (
                <span
                  {...row.getToggleRowExpandedProps({
                    style: {
                      // We can even use the row.depth property
                      // and paddingLeft to indicate the depth
                      // of the row
                      paddingLeft: `${row.depth * 2}rem`,
                    },
                  })}
                >
                  {row.isExpanded ? "-" : "+"}
                </span>
              ) : null,
          },
          ...columns,
        ]);
    }
  );

  const { pageIndex, pageCount } = props;
  // Também depende de pageCount: com paginação manual o react-table recusa
  // gotoPage enquanto o total de páginas ainda não chegou da API (ex: página
  // inicial vinda da URL), então é preciso tentar de novo quando ele chegar.
  useEffect(() => {
    if (pageIndex !== undefined && pageIndex !== dataTable.state.pageIndex) {
      dataTable.gotoPage(pageIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageCount]);

  const { sortBy } = props;
  const chaveOrdenacao = (ordenacao: { id: string; desc?: boolean }[]) =>
    ordenacao.map(({ id, desc }) => `${id}:${!!desc}`).join(";");
  // "setSortBy" não passa pelo setOrder do stateReducer: não volta para quem
  // controla a ordenação
  useEffect(() => {
    if (
      sortBy &&
      chaveOrdenacao(sortBy) !== chaveOrdenacao(dataTable.state.sortBy)
    ) {
      dataTable.setSortBy(sortBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  let rows = pagination ? dataTable.page : dataTable.rows;

  return (
    <>
      {isSearchable && (
        <GlobalFilter
          preGlobalFilteredRows={dataTable.preGlobalFilteredRows}
          // A busca controlada de fora (setSearch + searchValue, ex.: vinda da
          // URL) prevalece; sem controle, vale o estado da própria tabela
          globalFilter={
            props.setSearch && props.searchValue !== undefined
              ? props.searchValue
              : dataTable.state.globalFilter || props["searchValue"]
          }
          setGlobalFilter={dataTable.setGlobalFilter}
          searchBoxClass={props["searchBoxClass"]}
        />
      )}
      {props.data.length ? (
        <>
          <div
            className="table-responsive"
            style={{
              position: "relative",
            }}
          >
            <table
              {...dataTable.getTableProps()}
              className={classNames(
                "table table-centered table-striped react-table",
                props["tableClass"]
              )}
              style={
                props.tableMinWidth
                  ? { minWidth: props.tableMinWidth }
                  : undefined
              }
            >
              <thead className={props["theadClass"]}>
                {(dataTable.headerGroups || []).map((headerGroup: any) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {(headerGroup.headers || []).map((column: any) => (
                      <th
                        {...column.getHeaderProps(
                          column.sort &&
                            typeof column.getSortByToggleProps === "function"
                            ? column.getSortByToggleProps()
                            : undefined
                        )}
                        className={classNames(
                          "align-middle",
                          column.headerClassName,
                          {
                            sorting_desc: column.isSortedDesc === true,
                            sorting_asc: column.isSortedDesc === false,
                            sortable: column.sort === true,
                          }
                        )}
                        style={{
                          position: "sticky",
                          top: 0,

                          ...(column.isSticky
                            ? {
                                left: 0,
                                zIndex: 4,
                              }
                            : {
                                zIndex: 3,
                              }),

                          background: "#fff",
                          // O tema reserva 30px à direita do th para as setas
                          // antigas (pseudo-elementos); aqui o ícone vem junto
                          // do texto, e o espaço só empurrava o ícone para fora
                          paddingRight: "0.85rem",
                          minWidth: column.isSticky ? "200px" : undefined,
                          width: column.isSticky ? "200px" : column.fixedWidth,
                        }}
                      >
                        {isSortable && column.sort ? (
                          // Texto e ícone na mesma linha; a largura da coluna
                          // precisa caber os dois (o texto nunca é cortado)
                          <span className="d-inline-flex align-items-center">
                            {column.render("Header")}
                            {/* Indica que a coluna é ordenável e o sentido atual */}
                            <i
                              className={classNames("mdi ms-1 flex-shrink-0", {
                                "mdi-swap-vertical text-muted opacity-50":
                                  !column.isSorted,
                                "mdi-arrow-up text-primary":
                                  column.isSorted && !column.isSortedDesc,
                                "mdi-arrow-down text-primary":
                                  column.isSorted && column.isSortedDesc,
                              })}
                            />
                          </span>
                        ) : (
                          column.render("Header")
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody
                className={classNames("align-middle", {
                  "table-com-sub-linhas": !!props.getSubRows,
                })}
                {...dataTable.getTableBodyProps()}
              >
                {(rows || []).map((row: any, i: number) => {
                  dataTable.prepareRow(row);
                  const subRows = props.getSubRows?.(row) || [];
                  const subRowsAbertas = props.isSubRowsOpen?.(row) ?? true;
                  const { key, ...rowProps } = row.getRowProps({
                    className:
                      classNames(props.rowClassName?.(row), {
                        // Listra pela posição entre as linhas principais: as
                        // sub-linhas (mesmo fechadas) não entram na alternância
                        "table-row-listrada": !!props.getSubRows && i % 2 === 0,
                      }) || undefined,
                  });
                  return (
                    <React.Fragment key={key}>
                      <tr
                        {...rowProps}
                        onClick={
                          props.onRowClick
                            ? (e: React.MouseEvent<HTMLTableRowElement>) => {
                                const alvo = e.target as HTMLElement;
                                if (!alvo.closest(SELETOR_INTERATIVO)) {
                                  props.onRowClick?.(row);
                                }
                              }
                            : undefined
                        }
                      >
                        {(row.cells || []).map((cell: any) => {
                          return (
                            <td
                              {...cell.getCellProps([
                                {
                                  className: cell.column.className,
                                },
                              ])}
                              style={
                                cell.column.isSticky
                                  ? {
                                      position: "sticky",
                                      left: 0,
                                      background: "#fff",
                                      zIndex: 2,
                                    }
                                  : undefined
                              }
                            >
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                      {subRows.map((item: any, index: number) => (
                        <tr
                          key={`${key}-sub-${index}`}
                          className={classNames("table-sub-row", {
                            "table-sub-row-fechada": !subRowsAbertas,
                          })}
                          aria-hidden={!subRowsAbertas || undefined}
                        >
                          {row.cells.map((cell: any) => {
                            const SubCell = cell.column.SubCell;

                            return (
                              <td
                                key={cell.column.id}
                                className={cell.column.className}
                              >
                                {/* Altura animável: a linha da tabela não
                                    anima sozinha, o conteúdo da célula sim */}
                                <div className="table-sub-row-conteudo">
                                  <div>
                                    {SubCell ? (
                                      <SubCell item={item} row={row} />
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagination && (
            <Pagination
              tableProps={dataTable}
              sizePerPageList={sizePerPageList}
            />
          )}
        </>
      ) : (
        <EmptyData />
      )}
    </>
  );
};

export default Table;
